import DataTable, { Column } from '@/components/CommonComponents/DataTable';
import ServerSearchFilter from '@/components/CommonComponents/ServerSearchFilter';
import StatCard from '@/components/CommonComponents/StatCard';
import { showToast } from '@/helper/toast.helper';
import { useExportFile } from '@/hooks/useExportFile';
import { usePermission } from '@/hooks/usePermission';
import { FaqService } from '@/services/faqManagmentService/faqApi';
import { AppDispatch, RootState } from '@/store';
import {
  deleteFaq,
  faqInfo,
  fetchFaqs,
  removeFaqFromList,
  updateFaqInList,
  updateFaqStats,
  updateFaqStatus,
} from '@/store/slices/faqsManagementSlice/faqSlice';
import { Faq, FaqStatus } from '@/types/faq.types';
import { Eye, MessagesSquare, Paperclip } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import DeleteModal from '../CommonComponents/DeleteModal';
import Pagination from '../CommonComponents/Pagination';
import ViewDetailsModal from '../CommonComponents/ViewDetailsModal';
import PageHeaderWrapper from '../Layout/PageHeaderWrapper';

export default function FaqManagement() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { exportFile } = useExportFile();

  const {
    list,
    faqInfoCount,
    loading: loadingTableData,
  } = useSelector((state: RootState) => state.faq);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const totalRecords = list.meta.count;
  const totalPages = list.meta.totalPages;

  const handlePageChange = (page: number) => setCurrentPage(page);

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [faqIdToDelete, setfaqIdToDelete] = useState<number | null>(null);
  const [faqNameToDelete, setFaqNameToDelete] = useState<string>('');
  const [filteredFaqs, setFilteredFaqs] = useState<Faq[]>([]);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Faq | null>(null);
  const [filters, setFilters] = useState<{
    search?: string;
    status?: FaqStatus | null;
  }>({
    search: '',
    status: null,
  });

  const permissions = {
    create: usePermission('write', 'faqs'),
    edit: usePermission('edit', 'faqs'),
    delete: usePermission('delete', 'faqs'),
    export: usePermission('export', 'faqs'),
  };

  useEffect(() => {
    dispatch(
      fetchFaqs({
        page: currentPage,
        limit: pageSize,
        search: filters.search || undefined,
        status: filters.status || null,
      }),
    );
  }, [dispatch, currentPage, pageSize, filters]);

  useEffect(() => {
    dispatch(faqInfo());
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;

    const buildRows = async () => {
      if (!list.data || list.data.length === 0) {
        setFilteredFaqs([]);
        return;
      }

      const rows: Faq[] = await Promise.all(
        list.data.map(async (faq) => {
          return {
            ...faq,
            _permissions: permissions,
          };
        }),
      );

      if (!cancelled) {
        setFilteredFaqs(rows);
      }
    };

    buildRows();

    return () => {
      cancelled = true;
    };
  }, [list.data]);

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedItem(null);
  };

  const handleStatusToggle = async (id: string, newStatus: boolean) => {
    const faqId = Number(id);
    const selectedFaq = list.data.find((faq) => faq.id === faqId);

    if (!selectedFaq) return;

    const oldStatus = selectedFaq.status;
    const statusValue = newStatus ? FaqStatus.ACTIVE : FaqStatus.INACTIVE;
    const oldStatusBoolean = oldStatus === FaqStatus.ACTIVE;

    dispatch(
      updateFaqInList({
        id: faqId,
        updates: { status: statusValue },
      }),
    );

    dispatch(
      updateFaqStats({
        statusChange: { oldStatus: oldStatusBoolean, newStatus },
      }),
    );

    try {
      await dispatch(
        updateFaqStatus({
          id: faqId,
          payload: { status: statusValue },
        }),
      ).unwrap();
    } catch (error: any) {
      dispatch(
        updateFaqInList({
          id: faqId,
          updates: { status: oldStatus },
        }),
      );

      dispatch(
        updateFaqStats({
          statusChange: { oldStatus: newStatus, newStatus: oldStatusBoolean },
        }),
      );

      showToast.error(
        typeof error === 'string' ? error : error?.message || 'Failed to update FAQ status',
      );
    }
  };

  const handleEditFaqs = (row: Faq) => {
    navigate(`/dashboard/faqs/edit/${row.id}`, {
      state: { from: 'faq-management', survey: row },
    });
  };

  const handleCreateFaq = () => {
    navigate('/dashboard/faqs/create');
  };

  const handleExport = () => {
    // Implement export logic
    exportFile(() => FaqService.exportFaqs(), `faqs_${Date.now()}.csv`);
  };

  const openDeleteModal = (id: number, name: string) => {
    setfaqIdToDelete(id);
    setFaqNameToDelete(name);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!faqIdToDelete) return;

    const selectedFaq = list.data.find((faq) => faq.id === faqIdToDelete);
    const wasActive = selectedFaq?.status === FaqStatus.ACTIVE;

    dispatch(removeFaqFromList(faqIdToDelete));

    dispatch(updateFaqStats({ deleted: true }));
    if (wasActive) {
      dispatch(
        updateFaqStats({
          statusChange: { oldStatus: true, newStatus: false },
        }),
      );
    } else {
      dispatch(
        updateFaqStats({
          statusChange: { oldStatus: false, newStatus: false },
        }),
      );
    }

    setIsDeleteModalOpen(false);
    const deletedId = faqIdToDelete;
    setfaqIdToDelete(null);
    setFaqNameToDelete('');

    try {
      await dispatch(deleteFaq(deletedId)).unwrap();
    } catch (error: any) {
      showToast.error(typeof error === 'string' ? error : error?.message || 'Failed to delete FAQ');

      dispatch(fetchFaqs({ page: currentPage, limit: pageSize }));
      dispatch(faqInfo());
    }
  };

  const handleFilterChange = (filtersInput: Record<string, any>) => {
    const search = filtersInput.search || '';
    let statusFetched = filtersInput.status?.toUpperCase() || null;

    if (statusFetched === 'ALL') {
      statusFetched = null;
    }

    setFilters({
      search,
      status: statusFetched,
    });

    setCurrentPage(1);
  };

  const faqColumns: Column<Faq>[] = [
    { key: 'id', header: 'Q ID' },
    {
      key: 'question',
      header: 'Question?',
      render: (value) => {
        const maxLength = 100;
        const text = String(value || '');
        return (
          <span title={text}>
            {' '}
            {text.length > maxLength ? `${text.substring(0, maxLength)}...` : text}
          </span>
        );
      },
    },
    {
      key: 'answer',
      header: 'Answer',
      render: (value) => {
        const maxLength = 75;
        const text = String(value || '');
        return (
          <span title={text}>
            {' '}
            {text.length > maxLength ? `${text.substring(0, maxLength)}...` : text}
          </span>
        );
      },
    },
    {
      key: 'createdAt',
      header: 'Created On',
      render: (value) => <span>{value ? value.split('T')[0] : '-'}</span>,
    },
    {
      key: 'updatedAt',
      header: 'Updated On',
      render: (value) => <span>{value ? value.split('T')[0] : '-'}</span>,
    },
    { key: 'status', header: 'Status' },
  ];

  const FaqStatsData = [
    {
      id: 'total',
      title: 'Total Questions',
      value: faqInfoCount.totalFaqs || 0,
      icon: Paperclip,
      iconBg: 'bg-[#E79C4CCC]',
    },
    {
      id: 'active',
      title: 'Active Questions',
      value: faqInfoCount.activeFaqs || 0,
      icon: MessagesSquare,
      iconBg: 'bg-[#F0DF4ACC]',
    },
    {
      id: 'inactive',
      title: 'Inactive Questions',
      value: faqInfoCount.inactiveFaqs || 0,
      icon: Eye,
      iconBg: 'bg-[#0BA96F80]',
    },
  ];

  return (
    <PageHeaderWrapper
      title="FAQs Management"
      description="Manage and monitor FAQs questions and answers."
      onAdd={handleCreateFaq}
      onExport={handleExport}
      addLabel="Add New FAQ"
      exportLabel="Export"
      permissions={permissions}
    >
      <div className="space-y-4">
        {/* Server Search + Filter */}
        <ServerSearchFilter
          fields={[
            {
              type: 'search',
              key: 'search',
              placeholder: 'Search items...',
            },
            {
              type: 'radio-select',
              key: 'status',
              label: 'Status',
              options: ['Active', 'Inactive', 'Draft'],
            },
          ]}
          onChange={handleFilterChange}
          noBg
        />

        {/* Stats Card */}
        <div className="gap-4 pt-2 justify-end grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
          {FaqStatsData.map((stat) => (
            <StatCard
              key={stat.id}
              title={stat.title}
              value={stat.value}
              icon={stat.icon}
              iconBg={stat.iconBg}
            />
          ))}
        </div>

        <div className="flex-1">
          <>
            {/* Data Table */}
            <DataTable<Faq>
              listTitle=""
              columns={faqColumns}
              data={filteredFaqs}
              loading={loadingTableData}
              onView={(row) => {
                setSelectedItem(row);
                setIsViewModalOpen(true);
              }}
              onEdit={(row) => {
                if (!row._permissions?.edit) return;
                handleEditFaqs(row);
              }}
              onDelete={(row) => {
                if (!row._permissions?.delete) return;
                openDeleteModal(row.id, row.question);
              }}
              onStatusToggle={handleStatusToggle}
              permissions={permissions}
            />
            {/* Pagination */}
            <Pagination
              totalRecords={totalRecords}
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              onPageSizeChange={handlePageSizeChange}
              onPageChange={handlePageChange}
            />
          </>
        </div>

        {/* View Modal */}
        {isViewModalOpen && selectedItem && (
          <ViewDetailsModal
            isOpen={isViewModalOpen}
            onClose={closeViewModal}
            title="FAQ Question Details"
            data={selectedItem}
            fields={[
              { label: 'QID', value: String(selectedItem.id) },
              { label: 'FAQ Question', value: selectedItem.question },
              { label: 'Answer', value: selectedItem.answer, fullWidth: true },
              {
                label: 'Created On',
                value: new Date(selectedItem.createdAt).toLocaleDateString('en-GB'),
              },
              {
                label: 'Status',
                value:
                  selectedItem.status === FaqStatus.ACTIVE
                    ? 'Active'
                    : selectedItem.status === FaqStatus.DRAFT
                    ? 'Draft'
                    : 'Inactive',
                list: true,
                fullWidth: true,
              },
            ]}
          />
        )}
      </div>

      {/* Delete Modal */}
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setfaqIdToDelete(null);
          setFaqNameToDelete('');
        }}
        onDelete={handleDelete}
        itemName={faqNameToDelete}
        pageTitle="Question"
      />
    </PageHeaderWrapper>
  );
}
